import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { Row } from "@/components";
import { FormDateInput } from "@/components/form/FormDateInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useUpdatePlacement } from "@/hooks/mutations/useUpdatePlacement";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { useSetPlacement } from "@/hooks/sets/useSetPlacement";
import { useSetRequisition } from "@/hooks/sets/useSetRequisition";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { mutations } from "@/lib/mutations";
import { requiredDateSchema } from "@/lib/schemas";
import { CandidacyPlacement, CandidacyStatus, PlacementStatus } from "@/types";

const employmentEndedSchema = z.object({
  dateEmploymentEnded: requiredDateSchema,
  employmentEndedReason: z.string().min(1, "Reason is required"),
});

export const EmploymentEndedDialog = ({
  open,
  onOpenChange,
  placements,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placements: CandidacyPlacement[];
}) => {
  /* Contexts */
  const { candidacyId } = useSheetScope();

  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo } = useGetCandidacyInfo({ candidacyId });

  /* Database Updates */
  // Updates
  const { mutate: updatePlacement } = useUpdatePlacement();

  /* Cache Updates */
  // Sets
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();
  const setPlacement = useSetPlacement();
  const setRequisition = useSetRequisition();

  const form = useForm<z.infer<typeof employmentEndedSchema>>({
    resolver: zodResolver(employmentEndedSchema),
    defaultValues: {
      dateEmploymentEnded: "",
      employmentEndedReason: "",
    },
  });

  const handleSubmit = (data: z.infer<typeof employmentEndedSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Employment ended",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    if (placements.length === 0) return;

    const updatePlacementRequest = {
      dateEmploymentEnded: dates.convertStringDateToIso(
        data.dateEmploymentEnded,
      ),
      employmentEndedReason: data.employmentEndedReason,
      status: PlacementStatus.COMPLETED,
    };

    mutations.updatePlacement({
      placementId: placements[0].id,
      setCandidacyInfo,
      setPlacement,
      setRequisition,
      onSuccess: () => {
        setCandidacyInAllLists({
          candidacyId,
          lastCandidacyStatusChange: {
            createdAt: new Date().toISOString(),
            fromStatus: candidacyInfo?.status ?? null,
            toStatus: CandidacyStatus.EMPLOYMENT_ENDED,
            statusChangeReason: null,
            byAgent: false,
          },
        });
        setCandidacyInfo({
          candidacyId,
          status: CandidacyStatus.EMPLOYMENT_ENDED,
          lastStatusChangeByAgent: false,
        });
        form.reset();
        onOpenChange(false);
      },
      updatePlacement,
      updatePlacementRequest,
    });
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mark employment as ended</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormDateInput
              fieldName="dateEmploymentEnded"
              form={form}
              label="Employment ended date"
            />
            <FormField
              control={form.control}
              name="employmentEndedReason"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Employment ended reason</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end">
              <Button className="rounded-full" type="submit">
                Submit
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
